import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService, AI_DETECTION_ENDPOINTS, CATEGORY } from '@core';
import { DetectionTaskStarted, DetectionTaskStatusResponse } from '@core/models/project.models';
import { ApiResponse } from '@core/models/api.models';

@Injectable()
export class PlumbingApiService {
  private readonly api = inject(ApiService);

  triggerPointProcess(projectId: number, scale: string): Observable<ApiResponse<DetectionTaskStarted>> {
    return this.api.post<DetectionTaskStarted>(AI_DETECTION_ENDPOINTS[CATEGORY.FIXTURES.numericId].preProcess!, {
      project_id: projectId,
      file_type_id: CATEGORY.FIXTURES.numericId,
      scale,
    });
  }

  pollTaskStatus(taskId: string): Observable<DetectionTaskStatusResponse> {
    return this.api.get<DetectionTaskStatusResponse>(
      `${AI_DETECTION_ENDPOINTS[CATEGORY.FIXTURES.numericId].taskStatus}/${taskId}`,
    ) as unknown as Observable<DetectionTaskStatusResponse>;
  }

  pollUntilComplete(taskId: string, stop$: Subject<void>): Observable<void> {
    const INITIAL_MS = 3_000;
    const MAX_MS     = 30_000;

    return new Observable<void>((observer) => {
      let timerId: ReturnType<typeof setTimeout> | null = null;
      let cancelled = false;
      let attempt   = 0;

      const scheduleNext = () => {
        const delay = Math.min(INITIAL_MS * (2 ** attempt++), MAX_MS);
        timerId = setTimeout(poll, delay);
      };

      const poll = () => {
        if (cancelled) return;
        this.pollTaskStatus(taskId)
          .pipe(takeUntil(stop$))
          .subscribe({
            next: (response: DetectionTaskStatusResponse) => {
              const progress = Number(response?.progress ?? 0);
              if (response?.success === false || response?.state === 'FAILURE') {
                observer.error(new Error(response?.message ?? 'Plumbing processing failed.'));
              } else if (progress >= 100) {
                observer.next();
                observer.complete();
              } else {
                scheduleNext();
              }
            },
            error: (err) => observer.error(err),
          });
      };

      const stopSub = stop$.subscribe(() => {
        cancelled = true;
        if (timerId !== null) clearTimeout(timerId);
      });

      scheduleNext();

      return () => {
        cancelled = true;
        if (timerId !== null) clearTimeout(timerId);
        stopSub.unsubscribe();
      };
    });
  }
}
